import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCustomers, getCustomer, createCustomer, updateCustomer, deleteCustomer, previewCustomerNumber } from "@/api/customers.api";
import { toast } from "sonner";
import type { CustomerWithEmails, UpdateCustomerInput } from "@/features/customers/types/customer.types";

// Re-export types for convenience
export type { CustomerWithEmails, CreateCustomerInput, UpdateCustomerInput } from "@/features/customers/types/customer.types";

export function useCustomers() {
  return useQuery<CustomerWithEmails[]>({
    queryKey: ["customers"],
    queryFn: getCustomers,
  });
}

export function useCustomer(id: string) {
  return useQuery<CustomerWithEmails>({
    queryKey: ["customers", id],
    queryFn: () => getCustomer(id),
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCustomer,
    onSuccess: async (newCustomer) => {
      // Wait for invalidation to complete before showing success
      await queryClient.invalidateQueries({ queryKey: ["customers"] });

      // Ensure the new customer is in the cache
      queryClient.setQueryData<CustomerWithEmails[]>(["customers"], (old) => {
        if (!old) return [newCustomer];
        // Check if customer already exists (from invalidation)
        const exists = old.some(c => c.id === newCustomer.id);
        if (exists) return old;
        // Otherwise add it
        return [newCustomer, ...old];
      });

      toast.success("Kunde erfolgreich erstellt");
    },
    onError: () => {
      toast.error("Fehler beim Erstellen des Kunden");
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerInput }) =>
      updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Kunde erfolgreich aktualisiert");
    },
    onError: () => {
      toast.error("Fehler beim Aktualisieren des Kunden");
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Kunde erfolgreich gelöscht");
    },
    onError: (error: Error) => {
      const message = error.message.includes("restrict") || error.message.includes("violates foreign key")
        ? "Kunde kann nicht gelöscht werden, weil noch Rechnungen mit diesem Kunden verknüpft sind"
        : "Fehler beim Löschen des Kunden";
      toast.error(message);
    },
  });
}

export function usePreviewCustomerNumber(enabled: boolean = true) {
  return useQuery({
    queryKey: ["customer-number-preview"],
    queryFn: previewCustomerNumber,
    enabled,
    staleTime: 0, // Always fetch fresh
    gcTime: 0, // Don't cache
  });
}
